/**
 * Utility functions for JWT token operations
 */

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token string
 * @returns {object|null} - Decoded token payload or null if invalid
 */
export const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get organization ID from JWT token
 * @returns {number|null} - Organization ID or null if not found
 */
export const getOrganizationId = () => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) {
      console.warn('No token found in sessionStorage');
      return null;
    }
    
    const decoded = decodeToken(token);
    if (!decoded) {
      console.warn('Failed to decode token');
      return null;
    }
    
    // The backend uses "OrgID" claim (see UserRepository.cs line 89)
    // ASP.NET Core stores custom claims as-is, so check for "OrgID" directly
    // Also check common variations and all keys
    let orgId = decoded.OrgID || 
                decoded['OrgID'] ||
                decoded.organizationID || 
                decoded.organizationId ||
                decoded.OrganizationID;
    
    // If still not found, check all keys (ASP.NET might use different claim types)
    if (!orgId) {
      for (const key in decoded) {
        const value = decoded[key];
        // Check if key contains "org" or if value looks like an org ID
        if ((key.toLowerCase().includes('org') || 
             key.toLowerCase().includes('organization')) && 
            value && !isNaN(parseInt(value))) {
          orgId = value;
          break;
        }
      }
    }
    
    // Debug: log the decoded token to help troubleshoot
    if (!orgId) {
      console.warn('Organization ID not found in token. Available claims:', Object.keys(decoded));
    }
    
    return orgId ? parseInt(orgId) : null;
  } catch (error) {
    console.error('Error getting organization ID:', error);
    return null;
  }
};

/**
 * Get user information from JWT token
 * @returns {object|null} - User info object or null
 */
export const getUserInfo = () => {
  try {
    const token = sessionStorage.getItem('token');
    if (!token) return null;
    
    const decoded = decodeToken(token);
    if (!decoded) return null;
    
    return {
      email: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/email'] || decoded.email,
      firstName: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] || decoded.FirstName || decoded.firstName,
      lastName: decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] || decoded.LastName || decoded.lastName,
      organizationId: getOrganizationId()
    };
  } catch (error) {
    console.error('Error getting user info:', error);
    return null;
  }
};

